import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import produce from 'utils/produce';
import classNames from 'classnames';
import { formatDate, parseDate } from 'utils/format';

import { Card, Typography, Button, Grid } from 'components/base';

import Icon, { ICON_TYPE } from 'components/icon';
import useEditorContext from '../context/editorContext';

export const parseTimestamp = timestamp => {
  return parseDate(timestamp, 'YYYY-MM-DDTHH:mm:ssZ');
};

const PhotoThumb = props => {
  const handleOnSelect = e => {
    e.preventDefault();

    props.onToggle(!props.photo.selected);
  };

  const handleOnSelectCover = e => {
    e.preventDefault();

    props.onSelectCover();
  };

  const selectButtonClassName = classNames({
    'btn-circle': true,
    'btn-circle-select': true,
    selected: props.photo.selected,
  });

  const coverButtonClassName = classNames({
    'btn-circle': true,
    'btn-circle-cover': true,
    'text-light': !props.cover,
    selected: props.cover,
  });

  return (
    <Card
      className="card-photo m-0"
      style={{
        backgroundImage: `url(${props.photo.url})`,
        backgroundSize: 'cover',
        backgroundPosition: '50% 50%',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Button
        size="xs"
        type="outline"
        className={coverButtonClassName}
        onClick={handleOnSelectCover}
      >
        Cover
      </Button>
      <Button size="xs" type="outline" className={selectButtonClassName} onClick={handleOnSelect}>
        <Icon type={ICON_TYPE.CHECK} />
      </Button>
    </Card>
  );
};

PhotoThumb.propTypes = {
  photo: PropTypes.object,
  cover: PropTypes.bool,
  onToggle: PropTypes.func,
  onSelectCover: PropTypes.func,
};

PhotoThumb.defaultProps = {
  cover: false,
};

const DESCRIPTION = (
  numberOfPhotos,
  numberOfSelectedPhotos,
  minimumNumberOfPhotos,
  maximumNumberOfPhotos,
) => {
  const minimumMorePhotos = minimumNumberOfPhotos - numberOfSelectedPhotos;
  const maximumMorePhotos =
    Math.min(numberOfPhotos, maximumNumberOfPhotos) - numberOfSelectedPhotos;

  if (minimumMorePhotos <= 0 && maximumMorePhotos > 0) {
    return 'Select more photos if you’d like, or add your print to cart.';
  }

  if (minimumMorePhotos > 0) {
    return `Select at least ${minimumMorePhotos} more ${minimumMorePhotos === 1 ? 'photo' : 'photos'
      }.`;
  }

  if (maximumMorePhotos < 0) {
    return `You can select only ${maximumNumberOfPhotos} photos.`;
  }

  return 'Proceed and add product into your cart!';
};

const InstagramPhotoList = props => {
  const editorContext = useEditorContext();
  const [months, setMonths] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [coverKey, setCoverKey] = useState();

  useEffect(() => {
    setMonths(
      new Array(12)
        .fill(0)
        .map((val, index) => index)
        .map(m => {
          const photos = props.photos
            .filter(photo => {
              if (parseTimestamp(photo.datetime).getMonth() === m) {
                return true;
              }

              return false;
            })
            .sort((a, b) => parseTimestamp(a.datetime) - parseTimestamp(b.datetime));

          return { date: new Date(props.year, m, 1), photos };
        })
        .filter(m => m.photos.length > 0),
    );
  }, [props.photos]);

  const handleOnChangeCoverKey = key => {
    setCoverKey(key);

    editorContext.updatePhoto(props.id, key, value => draft => {
      draft.validDomain = undefined;
      if (!(value.width > 0) || !(value.height > 0)) {
        draft.width = undefined;
        draft.height = undefined;
      }
    });
  };

  useEffect(() => {
    if (!coverKey && months.length > 0) {
      handleOnChangeCoverKey(months[0].photos[0].key);
    }
  }, [coverKey, months]);

  const handleOnToggleMonth = (e, index) => {
    e.preventDefault();

    setExpanded(val =>
      produce(val, draft => {
        if (val[index]) {
          delete draft[index];
        } else {
          draft[index] = true;
        }
      }),
    );
  };

  const handleOnSuccessClick = e => {
    e.preventDefault();

    props.onSuccess(coverKey);
  };

  const buttonDisabled =
    props.selectedNumberOfPhotos < props.minimumNumberOfPhotos ||
    props.selectedNumberOfPhotos > props.maximumNumberOfPhotos;

  return (
    <>
      <Card className="text-center mx-auto mw-480 mb-5">
        <Card.Body>
          <Typography.H4 className=" text-warning">
            {props.selectedNumberOfPhotos} PHOTOS SELECTED!
          </Typography.H4>
          <Typography.P className="mb-0">
            {DESCRIPTION(
              props.photos.length,
              props.selectedNumberOfPhotos,
              props.minimumNumberOfPhotos,
              props.maximumNumberOfPhotos,
            )}
          </Typography.P>
          <Button
            block
            type="success"
            onClick={handleOnSuccessClick}
            className="mt-1"
            disabled={buttonDisabled}
          >
            Add to Cart
          </Button>
        </Card.Body>
      </Card>

      {months.map((month, index) => {
        const numberOfMonthPhotos = month.photos.length;
        const numberOfMonthSelectedPhotos = month.photos.filter(photo => photo.selected).length;

        return (
          <Card key={month.date} className="mt-1">
            <Card.Body>
              <Typography.H5 className="mb-0">
                <small className="float-right">
                  {`${numberOfMonthSelectedPhotos} / ${numberOfMonthPhotos} selected`}
                  <Button className="p-0 ml-1" onClick={e => handleOnToggleMonth(e, index)}>
                    <Icon
                      type={ICON_TYPE.BACK}
                      className={expanded[index] ? 'icon-rotate-90' : 'icon-rotate-180'}
                    />
                  </Button>
                </small>
                <Button className="h5 p-0 mb-0" onClick={e => handleOnToggleMonth(e, index)}>
                  {formatDate(month.date, 'MMM')}
                </Button>
              </Typography.H5>
              {expanded[index] && (
                <Grid.Row noGutters className="mt-1">
                  {month.photos.map(photo => (
                    <Grid.Col key={photo.url} xs={4} sm={3} md={2} lg={1}>
                      <PhotoThumb
                        photo={photo}
                        cover={photo.key === coverKey}
                        onToggle={selected => props.onTogglePhoto(photo.key, selected)}
                        onSelectCover={() => handleOnChangeCoverKey(photo.key)}
                      />
                    </Grid.Col>
                  ))}
                </Grid.Row>
              )}
            </Card.Body>
          </Card>
        );
      })}
    </>
  );
};

InstagramPhotoList.propTypes = {
  id: PropTypes.string,
  year: PropTypes.number,
  selectedNumberOfPhotos: PropTypes.number,
  minimumNumberOfPhotos: PropTypes.number,
  maximumNumberOfPhotos: PropTypes.number,
  photos: PropTypes.array,
  onTogglePhoto: PropTypes.func,
  onSuccess: PropTypes.func,
};

export default InstagramPhotoList;
